import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { SignUpModal } from '@/components/modals/SignUpModal';
import { useCreateQuote } from '@/hooks/useCreateQuote';
import {
  FileText,
  FileSignature,
  ClipboardList,
  DollarSign,
  Receipt,
  ReceiptText,
  CalendarDays,
  ShoppingCart,
  Users,
  Wrench,
  Package,
} from 'lucide-react';

// Ícones PREENCHIDOS (branco sólido sobre a barra verde). O lucide é só contorno,
// então usamos SVGs preenchidos (fill=currentColor herda a cor do botão).
type IconProps = { className?: string };
const IconHome = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" /></svg>
);
const IconQuotes = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" /></svg>
);
const IconClients = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
);
const IconCatalog = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M20 2H4c-1.1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-.9-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z" /></svg>
);
const IconPlus = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><rect x="5" y="10.5" width="14" height="3" rx="1.5" /><rect x="10.5" y="5" width="3" height="14" rx="1.5" /></svg>
);

export function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // Papel → cota → plano → visitante: a regra mora em useCreateQuote porque o
  // dashboard tem o MESMO gesto num CTA próprio (ver o hook).
  const {
    createQuote: handleCreateQuote,
    signUpOpen: showSignUpModal,
    setSignUpOpen: setShowSignUpModal,
  } = useCreateQuote();

  const menuOptions = [
    {
      label: 'Orçamento',
      icon: FileText,
      bg: 'bg-[#2fae73]',
      onClick: () => {
        setMenuOpen(false);
        handleCreateQuote();
      },
    },
    {
      label: 'Contrato',
      icon: FileSignature,
      bg: 'bg-violet-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/contratos?new=true');
      },
    },
    {
      label: 'Ordem de serviço',
      icon: ClipboardList,
      bg: 'bg-orange-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/ordens-servico?new=true');
      },
    },
    {
      label: 'Lançamento',
      icon: DollarSign,
      bg: 'bg-green-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/financeiro?new=true');
      },
    },
    {
      label: 'Nota fiscal',
      icon: Receipt,
      bg: 'bg-indigo-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/nfse/nova');
      },
    },
    {
      label: 'Recibo',
      icon: ReceiptText,
      bg: 'bg-teal-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/recibos/novo');
      },
    },
    {
      label: 'Compromisso',
      icon: CalendarDays,
      bg: 'bg-sky-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/agenda/novo');
      },
    },
    {
      label: 'Venda rápida',
      icon: ShoppingCart,
      bg: 'bg-emerald-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/venda');
      },
    },
    {
      label: 'Cliente',
      icon: Users,
      bg: 'bg-blue-500',
      onClick: () => {
        setMenuOpen(false);
        navigate('/clients/new');
      },
    },
    {
      label: 'Serviço',
      icon: Wrench,
      bg: 'bg-violet-600',
      onClick: () => {
        setMenuOpen(false);
        navigate('/catalog/new?type=service');
      },
    },
    {
      label: 'placeholder',
      icon: Wrench,
      bg: '',
      onClick: () => {},
      isPlaceholder: true,
    },
    {
      label: 'Produto',
      icon: Package,
      bg: 'bg-emerald-600',
      onClick: () => {
        setMenuOpen(false);
        navigate('/catalog/new?type=product');
      },
    },
  ];

  const isHomeActive =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname === '/test-dashboard';

  // Cada item: conjunto ícone+nome. Ativo = branco cheio; inativo = branco 80%.
  const itemClass = (active: boolean) =>
    cn(
      'flex flex-col items-center gap-1.5 transition-colors',
      active ? 'text-white' : 'text-white/80 hover:text-white',
    );

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          "fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300",
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMenuOpen(false)}
      />

      {/* Quick Creation Menu Sheet */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 bg-white dark:bg-card rounded-t-[32px] shadow-[0_-8px_30px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out",
          menuOpen ? "translate-y-0" : "translate-y-full"
        )}
        style={{
          zIndex: 35,
          paddingBottom: "calc(var(--bottom-nav-h) + 16px)",
          paddingTop: "24px",
          WebkitMaskImage: 'radial-gradient(37px at 50% calc(100% - var(--bottom-nav-h)), transparent 36px, #000 37px)',
          maskImage: 'radial-gradient(37px at 50% calc(100% - var(--bottom-nav-h)), transparent 36px, #000 37px)',
        }}
      >
        <div className="max-h-[60vh] overflow-y-auto px-6">
          <div className="grid grid-cols-3 gap-x-2 gap-y-5 pb-2 pt-1">
            {menuOptions.map((opt, idx) => {
              if ('isPlaceholder' in opt && opt.isPlaceholder) {
                return <div key={`placeholder-${idx}`} className="h-14 w-14" aria-hidden="true" />;
              }
              const Icon = opt.icon;
              return (
                <button
                  key={opt.label}
                  onClick={opt.onClick}
                  className="flex flex-col items-center gap-1.5 transition-transform active:scale-95 group text-center"
                >
                  <div className={cn(
                    "flex h-14 w-14 items-center justify-center rounded-2xl text-white transition-transform group-hover:scale-105 shadow-md",
                    opt.bg
                  )}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <span className="text-[12px] font-semibold leading-tight text-gray-700 dark:text-gray-200 mt-1 max-w-[80px]">
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Barra ancorada no rodapé: cantos superiores arredondados, base reta; o
          pb-safe preenche a área segura em verde. A banda interna de 6px + o
          padding assimétrico centralizam o conjunto na FACE CLARA (mesmo princípio
          dos botões Button3D). */}
      <nav className="fixed inset-x-0 bottom-0 z-40 pb-safe">
        {/* Camada de FUNDO verde, com um ENTALHE (notch) transparente no topo-centro,
            recortado por `mask`. Ele recria o VÃO ao redor do "+" que a antiga "borda
            branca" ocupava — mas agora vazado (mostra o fundo da página atrás da nav),
            sem pintar branco. O "+" fica na camada de cima (o grid), então NÃO é
            recortado pela máscara. Notch de raio 36px vs. "+" de raio 30px = vão de 6px. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-t-3xl bg-[#2fae73] shadow-[0_-4px_20px_rgba(0,0,0,0.07)]"
          style={{
            WebkitMaskImage: 'radial-gradient(37px at 50% 0, transparent 36px, #000 37px)',
            maskImage: 'radial-gradient(37px at 50% 0, transparent 36px, #000 37px)',
          }}
        />
        <div
          className="relative grid grid-cols-5 items-center px-2 pt-2 pb-3.5"
          style={{ boxShadow: 'inset 0 -6px 0 rgba(16,78,52,0.24)' }}
        >
          <button onClick={() => navigate('/dashboard')} className={itemClass(isHomeActive)}>
            <IconHome className="h-7 w-7" />
            <span className="text-[13px] font-medium">Início</span>
          </button>

          <button onClick={() => navigate('/quotes')} className={itemClass(location.pathname === '/quotes')}>
            <IconQuotes className="h-7 w-7" />
            <span className="text-[13px] font-medium">Orçamentos</span>
          </button>

          {/* Coluna central: espaçador (do tamanho do ícone) + label, para o "Novo"
              alinhar com os demais nomes. O botão redondo é posicionado por cima. */}
          <div className="flex flex-col items-center gap-1.5 text-white">
            <div className="h-7" aria-hidden="true" />
            <span className="text-[13px] font-medium">Novo</span>
          </div>

          <button onClick={() => navigate('/clients')} className={itemClass(location.pathname === '/clients')}>
            <IconClients className="h-7 w-7" />
            <span className="text-[13px] font-medium">Clientes</span>
          </button>

          <button onClick={() => navigate('/catalog')} className={itemClass(location.pathname === '/catalog')}>
            <IconCatalog className="h-7 w-7" />
            <span className="text-[13px] font-medium">Catálogo</span>
          </button>

          {/* Botão "+" elevado: centro exatamente na borda superior da barra
              (metade para fora). O vão ao seu redor vem do notch na camada de fundo
              (acima) — aqui só a sombra de elevação, sem anel/borda pintada. */}
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Menu de criação rápida"
            className="absolute left-1/2 top-0 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#0d5537] text-white transition-all active:scale-95"
            style={{
              boxShadow: '0 9px 18px rgba(13,55,35,0.42)',
              transform: `translate(-50%, -50%) ${menuOpen ? 'rotate(45deg)' : 'rotate(0deg)'}`,
              transitionProperty: 'transform, background-color, box-shadow',
              transitionDuration: '300ms',
            }}
          >
            <IconPlus className="h-8 w-8" />
          </button>
        </div>
      </nav>

      <SignUpModal
        open={showSignUpModal}
        onOpenChange={setShowSignUpModal}
      />
    </>
  );
}
